<template>
  <ValidationObserver ref="obs" v-slot="{ invalid }">
    <v-form>
      <v-card :class="{ 'elevation-0': embedded, 'pa-0': embedded }">
        <v-toolbar dark color="primary" v-if="!embedded">
          <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
          <slot name="header"></slot>
        </v-toolbar>
        <v-card-text :class="{ 'pa-0': embedded }">
          <v-container grid-list-md :class="{ 'pa-0': embedded }">
            <v-layout wrap>
              <v-flex xs12 v-bind:md6="embedded">
                <v-menu
                  v-model="startDateDialog"
                  :close-on-content-click="false"
                  max-width="290"
                  v-bind:disabled="disabled"
                >
                  <template v-slot:activator="{ on }">
                    <ValidationProvider
                      name="start date"
                      rules="required"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        v-model="formattedStartDate"
                        label="First day that the unit was out of service?"
                        readonly
                        :error-messages="errors"
                        name="start date"
                        v-on="on"
                        append-icon="event"
                        :disabled="disabled"
                        @click:append="startDateDialog = true"
                      ></v-text-field>
                    </ValidationProvider>
                  </template>
                  <v-date-picker
                    v-model="value.startDate"
                    @change="startDateDialog = false; $emit('edit'); reactiveProp++"
                    no-title
                    :min="isoMinDate"
                    :max="maxDateOrEndDateMinusOne"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 v-bind:md6="embedded">
                <v-menu
                  v-model="endDateDialog"
                  :close-on-content-click="false"
                  max-width="290"
                  v-bind:disabled="disabled"
                >
                  <template v-slot:activator="{ on }">
                    <ValidationProvider
                      name="end date"
                      rules="required"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        v-model="formattedEndDate"
                        :label="endDateDescription"
                        readonly
                        :error-messages="errors"
                        name="end date"
                        v-on="on"
                        append-icon="event"
                        v-bind:disabled="disabled"
                        @click:append="endDateDialog = true"
                      ></v-text-field>
                    </ValidationProvider>
                  </template>
                  <v-date-picker
                    v-model="value.endDate"
                    @change="endDateDialog = false; $emit('edit'); reactiveProp++"
                    no-title
                    :min="minDateOrStartDatePlusOne"
                    :max="isoMaxDate"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-layout column class="mx-1">
                <ValidationProvider
                  name="reason"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    v-model="value.reason"
                    :label="reasonDescription"
                    name="reason"
                    maxlength="1024"
                    required
                    :disabled="disabled"
                  ></v-text-field>
                </ValidationProvider>
              </v-layout>
              <v-flex xs12 v-bind:md6="embedded">
                <ValidationProvider
                  name="category"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-select
                    v-model="value.categoryId"
                    :items="categories"
                    item-value="id"
                    item-text="name"
                    label="Category"
                    :error-messages="errors"
                    :disabled="disabled"
                    required
                  ></v-select>
                </ValidationProvider>
              </v-flex>
              <v-flex xs12 v-if="hasOpenDefects && !embedded">
                <v-alert
                  dense
                  border="left"
                  type="warning"
                >This unit has open scheduled off-road defects</v-alert>
              </v-flex>
              <v-flex xs12 v-if="!embedded">
                <ValidationProvider rules vid="returnedToService" v-slot="x">
                  <v-checkbox
                    label="Returned to Service"
                    v-model="value.returnedToService"
                    name="returnedToService"
                    :disabled="disabled"
                    @change='returnedToServiceChanged($event)'
                  ></v-checkbox>
                </ValidationProvider>
              </v-flex>
              <v-flex xs12 v-if="value.returnedToService && !embedded">
                <v-menu
                  v-model="returnedToServiceDateDialog"
                  :close-on-content-click="false"
                  max-width="290"
                  :disabled="disabled"
                >
                  <template v-slot:activator="{ on }">
                    <ValidationProvider
                      name="returned date"
                      rules="required_if:returnedToService,true"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        v-model="formattedReturnedToServiceDate"
                        :label="returnedToServiceDateDescription"
                        readonly
                        :error-messages="errors"
                        name="returned date"
                        v-on="on"
                        append-icon="event"
                        @click:append="returnedToServiceDateDialog = true"
                      ></v-text-field>
                    </ValidationProvider>
                  </template>
                  <v-date-picker
                    v-model="value.returnedToServiceDate"
                    @change="returnedToServiceDateDialog = false"
                    no-title
                    :min="minDateOrStartDate"
                    :max="isoMaxDate"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions v-if="!embedded">
          <v-spacer></v-spacer>
          <v-btn @click="close">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="disableSubmit"
            @click="submit"
          >{{ !editing ? 'Create New VOR' : 'Save Changes' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </ValidationObserver>
</template>
<script>
import moment from 'moment';
import FormValidation from '@/components/FormValidation.vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import Decimal from '@/components/Decimal.vue';
import Date from '@/components/Date.vue';
import { DateFormat } from '@/plugins/date-filter';

export default {
  props: {
    value: {
      required: true
    },
    embedded: {
      type: Boolean,
      default: false,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Decimal,
    Date,
    ValidationProvider,
    ValidationObserver
  },
  mixins: [FormValidation],
  computed: {
    formTitle() {
      return !this.editing ? 'Add VOR' : 'Edit VOR for Unit';
    },
    editing() {
      return !!this.value.id;
    },
    formattedStartDate: {
      get() {
        return this.reactiveProp && this.value.startDate ? moment(this.value.startDate).format(DateFormat.DefaultDisplay) : '';
      },
      set(value) {
        this.value.startDate = value;
        this.reactiveProp++;
      }
    },
    formattedEndDate: {
      get() {
        return this.reactiveProp && this.value.endDate ? moment(this.value.endDate).format(DateFormat.DefaultDisplay) : '';
      },
      set(value) {
        this.value.endDate = value;
        this.reactiveProp++;
      }
    },
    formattedReturnedToServiceDate() {
      return this.value.returnedToServiceDate ? moment(this.value.returnedToServiceDate).format(DateFormat.DefaultDisplay) : '';
    },
    isoMinDate() {
      return moment('1900-01-01').format(DateFormat.Canonical);
    },
    isoMaxDate() {
      return moment('3999-01-01').format(DateFormat.Canonical);
    },
    minDateOrStartDatePlusOne() {
      return this.value.startDate
        ? moment(this.value.startDate)
            .add(1, 'day')
            .format(DateFormat.Canonical)
        : this.isoMinDate;
    },
    minDateOrStartDate() {
      return this.value.startDate ? moment(this.value.startDate).format(DateFormat.Canonical) : this.isoMinDate;
    },
    maxDateOrEndDateMinusOne() {
      return this.value.endDate
        ? moment(this.value.endDate)
            .subtract(1, 'hour')
            .format(DateFormat.Canonical)
        : this.isoMaxDate;
    },
    endDateDescription() {
      return 'When is the unit expected to be returned to service?';
    },
    returnedToServiceDateDescription() {
      return 'When was the unit returned to service?';
    },
    reasonDescription() {
      return 'Why is the vehicle being taken off the road?';
    }
  },
  methods: {
    async submitForm() {
      const valid = await this.checkValidation();
      if (!valid) {
        return;
      }
      this.addOrUpdate();
    },
    addOrUpdate() {
      if (!this.editing) {
        this.$emit('add', { id: this.id, model: this.value });
      } else {
        this.$emit('edit', { id: this.id, model: this.value });
      }
    },
    close() {
      this.$emit('close');
      this.resetValidation();
    },
    resetValidation() {
      this.$nextTick(() => {
        if (this.$refs.obs) {
          this.$refs.obs.reset();
        }
      });
    },
    async checkUnitHasOpenDefect() {
      if (!this.value || !this.value.unitId) {
        return;
      }

      const response = await this.$api.get(`units/${this.value.unitId}/open-defects-count`);
      this.hasOpenDefects = response.data > 0;
    },
    async checkValidation() {
      return await this.$refs.obs.validate();
    },
    setStartDate(date) {
      this.formattedStartDate = date;
      this.$forceUpdate();
    },
    setEndDate(date) {
      this.formattedEndDate = date;
      this.$forceUpdate();
    },
    returnedToServiceChanged(val) {
      if (!val) {
      this.value.returnedToServiceDate = null;
      }

    },
    async loadCategories() {
      try {
        const response = await this.$api.get('vehicle-off-road-categories');
        this.categories = response.data;
      } catch (error) {
        this.$toast.error('Failed to load VOR categories');
      }
    }
  },
  data() {
    return {
      id: '',
      today: moment().format(DateFormat.Canonical),
      minDate: moment('1900-01-01').format(DateFormat.Canonical),
      startDateDialog: false,
      endDateDialog: false,
      returnedToServiceDateDialog: false,
      hasOpenDefects: false,
      reactiveProp: 1, //used to force computed to be reactive
      categories: []
    };
  },
  watch: {
    async value(val) {
      if (val.id) {
        this.id = val.id;
      } else {
        this.id = this.$uuid.v1();
      }
    }
  },
  async mounted() {
    await this.checkUnitHasOpenDefect();
    await this.loadCategories();
  }
};
</script>
